// This store is used to track credits and credit history

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import ListProjectCreditsHistory from '@/gql/billing/credits/ListProjectCreditsHistory.gql';

import { useLazyQuery } from '@vue/apollo-composable';

import { useProjectStore } from '@/stores/project';

export const useCreditsStore = defineStore('credits', () => {
  const projectStore = useProjectStore();

  const selectedProject = computed(() => {
    return projectStore.currentProject?.project ?? null;
  });

  const creditCount = computed(() => {
    if (!selectedProject.value?.credit_balance) return 0;
    return selectedProject.value?.credit_balance;
  });

  const creditCountString = computed(() => {
    if (creditCount.value === 1) return '1 credit';
    return `${creditCount.value} credits`;
  });

  const creditHistory = ref([]);

  function updateHistory(data) {
    if (!data) return;
    const {
      project_credits: projectCredits,
      project_credit_usage_history: creditUsage,
    } = data;
    if (!projectCredits) {
      creditHistory.value = [...data];
      return;
    }
    const projectCreditsWithUsage = projectCredits.map((credit) => ({
      ...credit,
      credit_remaining: credit.credit_amount,
    }));
    if (creditUsage && creditUsage.length > 0) {
      creditUsage.forEach((usage) => {
        const credit = projectCreditsWithUsage.find(
          (c) => c.id === usage.credit_id
        );
        if (!credit) return;
        credit.credit_remaining -= usage.amount;
      });
    }
    creditHistory.value = [...projectCreditsWithUsage];
  }

  const variables = computed(() => ({
    projectId: selectedProject.value?.id,
  }));
  const {
    load,
    refetch,
    onError,
    loading: loadingHistory,
    onResult,
  } = useLazyQuery(ListProjectCreditsHistory, variables.value, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  function fetch() {
    return new Promise((resolve, reject) => {
      onResult(({ data }) => {
        if (data) {
          updateHistory(data);
          resolve(data);
        }
      });
      onError((error) => {
        reject(error);
      });
      load() || refetch();
    });
  }

  return {
    creditCount,
    creditCountString,
    fetch,
    loadingHistory,
    creditHistory,
  };
});
